import React, {ReactNode} from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'

import {mq} from '../../../utils/breakpoints'
import ToolTip from '../Tooltip'
import {TshirtSizesType} from 'types/Sizes'
import {ThemeType} from 'types/Theme'

type PropsType = {
  /** Color to be used as the background */
  backgroundColor: string
  /** Content to be displayed in the Tag */
  children: ReactNode
  /** ID for end to end testing and Fullstory monitoring */
  dataId?: string
  /** ID used to uniquely identify the Tag's tooltip */
  id: string
  /** The button click handler */
  onClick?: () => void
  /** Color to be used for the text */
  textColor: string
  /** Icon to be used in the Tag's tooltip */
  tooltipIcon?: ReactNode
  /** Content to be displayed in the Tag's tooltip */
  tooltipMessage?: ReactNode
  /** The size of the tag */
  size?: TshirtSizesType
  /** Whether the tag displays with alternative curved borders */
  hasAlternateRadius?: boolean
}

const TagWrapper = styled('span')(
  css`
    display: inline-block;
  `
)

const getTagElementDynamicStyles = (
  size: TshirtSizesType,
  theme: ThemeType
) => {
  let padding = ''
  switch (size) {
    case 'xs':
      padding = `1px ${theme.layout.spacing.s200}px`
      break

    case 'sm':
      padding = `${theme.layout.spacing.s200}px ${theme.layout.spacing.s300}px`
      break

    default:
      padding = `${theme.layout.spacing.s100}px ${theme.layout.spacing.s300}px`
      break
  }

  return css`
    line-height: ${size === 'sm'
      ? theme.fonts.lineHeight.xs
      : theme.fonts.lineHeight[size]};
    font-size: ${theme.fonts.size.xs};
    padding: ${padding};
  `
}

const TagElement = styled('span')<{
  textColor: string
  backgroundColor: string
  size: TshirtSizesType
  hasAlternateRadius: boolean
}>(
  ({theme, textColor, backgroundColor, size, hasAlternateRadius}) => css`
    display: inline-block;
    margin: 0;
    border-radius: ${hasAlternateRadius
      ? `${theme.layout.spacing.s300}px 0`
      : theme.layout.radius.sm}px;
    ${getTagElementDynamicStyles(size, theme)}
    font-weight: ${theme.fonts.weight.lg};

    background: ${backgroundColor};
    color: ${textColor};

    ${mq.desktopXs(css`
      vertical-align: text-top;
    `)}
  `
)

const Tag = ({
  children,
  dataId,
  id,
  tooltipMessage,
  tooltipIcon,
  textColor,
  onClick,
  backgroundColor,
  size = 'sm',
  hasAlternateRadius = false // TODO: clean up as part of ab_dq82hd-desktop-map experiment
}: PropsType) => (
  <TagWrapper data-id={dataId} onClick={onClick || null}>
    <ToolTip id={id} message={tooltipMessage} icon={tooltipIcon}>
      <TagElement
        textColor={textColor}
        backgroundColor={backgroundColor}
        size={size}
        hasAlternateRadius={hasAlternateRadius}
      >
        {children}
      </TagElement>
    </ToolTip>
  </TagWrapper>
)

export default Tag
